// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Main App Router
// Provably Fair Gaming on $FORGE
// ================================================================
import React, { useState } from 'react';
import { Routes, Route, Navigate, Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { CasinoProvider, useCasino } from './context/CasinoShell';
import { ToastProvider } from './shared/Toast';
import './shared/modern-ux.css';

// Auth pages (from template)
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PasswordChangePage from './pages/PasswordChangePage';
import NDASignaturePage from './pages/NDASignaturePage';
import AppTutorialPage from './pages/AppTutorialPage';
import OverwatchPage from './pages/OverwatchPage';

// Casino pages
import LandingPage from './pages/LandingPage';
import AgeVerifyPage from './pages/AgeVerifyPage';
import KYCVerifyPage from './pages/KYCVerifyPage';
import CasinoLobby from './pages/CasinoLobby';
import SlotsFloor from './pages/SlotsFloor';
import SlotMachinePage from './pages/SlotMachinePage';
import PlayerVault from './pages/PlayerVault';
import TransactionHistory from './pages/TransactionHistory';
import FairnessVerifier from './pages/FairnessVerifier';
import RewardsPage from './pages/RewardsPage';
import ResponsibleGamingPage from './pages/ResponsibleGamingPage';
import Leaderboard from './pages/Leaderboard';
import PlayerProfile from './pages/PlayerProfile';
import AdminDashboard from './pages/AdminDashboard';
import SportsBook from './pages/SportsBook';
import TablesFloor from './pages/TablesFloor';
import TableGamePage from './pages/TableGamePage';

// ============================================================
// ADMIN TEAM — Users who get owner-level access
// ============================================================
const ADMIN_TEAM = [
  'J.lutz@miskulintechnologies.com',
  'R.miskulin@miskulintechnologies.com',
  'rmiskulin@gmail.com',
  'threewayindustries@gmail.com',
  'desbachynski@gmail.com',
  'lucas.s.mason@gmail.com',
  'leemehling@hotmail.com',
  'cloricer77@gmail.com',
  'tyson@canstorr.com',
  'tyler.hodgson32@gmail.com',
  'jhansen860@yahoo.com',
  'littlesderek@gmail.com',
  'laken.weston@live.ca',
  'che@rosebudcannabisfarms.ca',
  'shanon@lanewaymedia.ca',
  'amandakristaleigh@hotmail.com',
  'dblahut@sastel.net',
  'demo@iqapps.dev',
];

export { ADMIN_TEAM };

// ============================================================
// Auth Guards
// ============================================================
function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return user ? children : <Navigate to="/login" />;
}

function AgeGate({ children }) {
  // Synchronous localStorage init — prevents flash of AgeVerifyPage on phones
  const [verified, setVerified] = useState(() => {
    try {
      const saved = localStorage.getItem('forgecasino_age_verified');
      if (saved) {
        const data = JSON.parse(saved);
        return !!data.verified;
      }
    } catch (e) { /* silent */ }
    return false;
  });

  if (!verified) {
    return <AgeVerifyPage onVerified={() => {
      localStorage.setItem('forgecasino_age_verified', JSON.stringify({
        verified: true,
        verifiedAt: new Date().toISOString(),
      }));
      setVerified(true);
    }} />;
  }
  return children;
}

function OnboardingGate({ children }) {
  const { user, onboardingStep, completeOnboardingStep } = useAuth();
  if (!user) return children;
  if (onboardingStep === 'password') return <PasswordChangePage onComplete={() => completeOnboardingStep('password')} />;
  if (onboardingStep === 'nda') return <NDASignaturePage onComplete={() => completeOnboardingStep('nda')} />;
  if (onboardingStep === 'tutorial') return <AppTutorialPage onComplete={() => completeOnboardingStep('tutorial')} />;
  return children;
}

function RealityCheckModal() {
  const { realityCheckDue, acknowledgeRealityCheck, sessionStats, getSessionDuration } = useCasino();
  if (!realityCheckDue) return null;

  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.modal}>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#B8973E" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
        <h2 style={modalStyles.title}>REALITY CHECK</h2>
        <p style={modalStyles.text}>You've been playing for <strong style={{ color: '#F0EDE6' }}>{getSessionDuration()} minutes</strong>.</p>
        <div style={modalStyles.stats}>
          <div style={modalStyles.stat}>
            <span style={modalStyles.statLabel}>SPINS</span>
            <span style={modalStyles.statValue}>{sessionStats.spinsThisSession}</span>
          </div>
          <div style={modalStyles.stat}>
            <span style={modalStyles.statLabel}>NET POSITION</span>
            <span style={{ ...modalStyles.statValue, color: sessionStats.netPosition >= 0 ? '#22C55E' : '#EF4444' }}>
              {sessionStats.netPosition >= 0 ? '+' : ''}${sessionStats.netPosition.toFixed(2)}
            </span>
          </div>
        </div>
        <p style={modalStyles.reminder}>
          Gambling should be fun. Consider taking a break or setting limits.
        </p>
        <button style={modalStyles.btn} onClick={acknowledgeRealityCheck}>
          CONTINUE PLAYING
        </button>
        <p style={modalStyles.helpline}>Alberta Helpline: 1-866-332-2322</p>
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
      height: '100vh', background: '#0A0E1A',
      fontFamily: "'Inter', -apple-system, sans-serif",
    }}>
      <div style={{
        fontSize: 14, fontWeight: 800, color: '#B8973E', letterSpacing: '0.2em',
        textShadow: '0 0 20px rgba(184,151,62,0.3)',
      }}>
        FORGE CASINO
      </div>
      <div style={{
        width: 40, height: 1, marginTop: 12,
        background: 'linear-gradient(90deg, transparent, #B8973E, transparent)',
      }} />
    </div>
  );
}

// ============================================================
// Casino Layout with Navigation — Sovereign Tech-Luxury
// ============================================================
function CasinoLayout() {
  const { user, signOut } = useAuth();
  const { forgeBalance } = useCasino();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const isAdmin = ADMIN_TEAM.map(e => e.toLowerCase()).includes((user?.email || '').toLowerCase());

  const NAV = [
    { path: '/lobby', iconKey: 'lobby', label: 'Lobby' },
    { path: '/slots', iconKey: 'slots', label: 'Slots' },
    { path: '/tables', iconKey: 'tables', label: 'Tables' },
    { path: '/sports', iconKey: 'sports', label: 'Sports' },
    { path: '/vault', iconKey: 'wallet', label: 'Wallet' },
    { path: '/rewards', iconKey: 'rewards', label: 'Rewards' },
    { path: '/leaderboard', iconKey: 'leaderboard', label: 'Ranks' },
    { path: '/vault/verify', iconKey: 'verify', label: 'Verify' },
    { path: '/responsible', iconKey: 'shield', label: 'Limits' },
    { path: '/profile', iconKey: 'settings', label: 'Profile' },
  ];

  if (isAdmin) {
    NAV.push({ path: '/admin', iconKey: 'verify', label: 'Admin' });
    NAV.push({ path: '/overwatch', iconKey: 'shield', label: 'Overwatch' });
  }

  // Inline SVG renderer for nav icons
  const NavIcon = ({ iconKey, size = 18, color = 'currentColor' }) => {
    const paths = {
      lobby: '<path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>',
      slots: '<rect x="2" y="4" width="20" height="16" rx="0"/><line x1="8" y1="4" x2="8" y2="20"/><line x1="16" y1="4" x2="16" y2="20"/>',
      tables: '<rect x="3" y="3" width="7" height="10" rx="0"/><rect x="14" y="3" width="7" height="10" rx="0"/><rect x="3" y="16" width="7" height="5" rx="0"/><rect x="14" y="16" width="7" height="5" rx="0"/>',
      sports: '<path d="M18 2H6v7a6 6 0 0012 0V2Z"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>',
      wallet: '<rect x="2" y="6" width="20" height="14" rx="0"/><path d="M2 10h20"/><circle cx="17" cy="14" r="1" fill="currentColor"/>',
      rewards: '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
      leaderboard: '<rect x="4" y="14" width="5" height="8" rx="0"/><rect x="10" y="8" width="5" height="14" rx="0"/><rect x="16" y="11" width="5" height="11" rx="0"/>',
      verify: '<path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>',
      shield: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>',
      settings: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>',
    };
    return (
      <span style={{ display: 'inline-flex', width: size, height: size, color }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={size} height={size}>
          <g dangerouslySetInnerHTML={{ __html: paths[iconKey] || paths.lobby }} />
        </svg>
      </span>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0A0E1A', color: '#F0EDE6', fontFamily: "'Inter', -apple-system, sans-serif" }}>
      {/* Top bar — premium glass */}
      <div style={navStyles.topBar}>
        <button style={navStyles.menuBtn} onClick={() => setMenuOpen(!menuOpen)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {menuOpen
              ? <path d="M18 6L6 18M6 6l12 12" />
              : <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>
            }
          </svg>
        </button>
        <span style={navStyles.logo}>FORGE CASINO</span>
        <div style={navStyles.topRight}>
          <span style={navStyles.balance}>${forgeBalance.toFixed(2)}</span>
        </div>
      </div>

      {/* Side menu overlay */}
      {menuOpen && <div style={navStyles.overlay} onClick={() => setMenuOpen(false)} />}

      {/* Side menu — glass panel */}
      <div style={{ ...navStyles.sideMenu, transform: menuOpen ? 'translateX(0)' : 'translateX(-100%)' }}>
        <div style={navStyles.sideHeader}>
          <div style={navStyles.sideLogoMark} />
          <span style={navStyles.sideLogo}>FORGE CASINO</span>
        </div>
        <div style={navStyles.sideBalance}>
          <span style={navStyles.sideBalLabel}>BALANCE</span>
          <span style={navStyles.sideBalVal}>${forgeBalance.toFixed(2)}</span>
        </div>
        {NAV.map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            style={({ isActive }) => ({
              ...navStyles.navLink,
              background: isActive ? 'rgba(184, 151, 62, 0.1)' : 'transparent',
              color: isActive ? '#B8973E' : '#8B949E',
              borderLeft: isActive ? '2px solid #B8973E' : '2px solid transparent',
            })}
            onClick={() => setMenuOpen(false)}
          >
            <NavIcon iconKey={item.iconKey} size={16} />
            <span>{item.label}</span>
          </NavLink>
        ))}
        <div style={navStyles.sideFooter}>
          <button style={navStyles.logoutBtn} onClick={() => { signOut(); navigate('/login'); }}>
            LOGOUT
          </button>
        </div>
      </div>

      {/* Bottom mobile nav — sharp glass bar */}
      <div style={navStyles.bottomNav}>
        {NAV.slice(0, 6).map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            style={({ isActive }) => ({
              ...navStyles.bottomItem,
              color: isActive ? '#B8973E' : '#4B5563',
            })}
          >
            <NavIcon iconKey={item.iconKey} size={18} />
            <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{item.label}</span>
          </NavLink>
        ))}
      </div>

      {/* Content */}
      <div style={{ paddingBottom: 70 }}>
        <Outlet />
      </div>

      {/* Reality Check Modal */}
      <RealityCheckModal />
    </div>
  );
}

// ============================================================
// Main App
// ============================================================
export default function App() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;

  return (
    <ToastProvider position="top-right">
      <CasinoProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={user ? <Navigate to="/lobby" /> : <LoginPage />} />
          <Route path="/signup" element={user ? <Navigate to="/lobby" /> : <SignUpPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/age-verify" element={<AgeVerifyPage onVerified={() => {}} />} />

          {/* Protected casino routes */}
          <Route element={
            <PrivateRoute>
              <AgeGate>
                <OnboardingGate>
                  <CasinoLayout />
                </OnboardingGate>
              </AgeGate>
            </PrivateRoute>
          }>
            <Route path="/lobby" element={<CasinoLobby />} />
            <Route path="/slots" element={<SlotsFloor />} />
            <Route path="/slots/:gameId" element={<SlotMachinePage />} />
            <Route path="/tables" element={<TablesFloor />} />
            <Route path="/tables/:gameId" element={<TableGamePage />} />
            <Route path="/sports" element={<SportsBook />} />
            <Route path="/vault" element={<PlayerVault />} />
            <Route path="/vault/history" element={<TransactionHistory />} />
            <Route path="/vault/verify" element={<FairnessVerifier />} />
            <Route path="/kyc" element={<KYCVerifyPage />} />
            <Route path="/rewards" element={<RewardsPage />} />
            <Route path="/responsible" element={<ResponsibleGamingPage />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/profile" element={<PlayerProfile />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/overwatch" element={<OverwatchPage />} />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" />} />
        <Route path="/demo-management" element={<ProtectedRoute><DemoManagementPage /></ProtectedRoute>} />

        </Routes>
      </CasinoProvider>
    </ToastProvider>
  );
}

// ============================================================
// Styles
// ============================================================
// ============================================================
// Styles — Sovereign Tech-Luxury Design System
// ============================================================
const navStyles = {
  topBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px',
    background: 'rgba(10, 14, 26, 0.95)',
    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(184, 151, 62, 0.15)',
    position: 'sticky', top: 0, zIndex: 100,
  },
  menuBtn: {
    background: 'none', border: 'none', color: '#8B949E', cursor: 'pointer',
    padding: '6px', display: 'flex', alignItems: 'center',
  },
  logo: {
    fontSize: 12, fontWeight: 800, color: '#B8973E', letterSpacing: '0.2em',
    fontFamily: "'Inter', sans-serif",
    textShadow: '0 0 12px rgba(184,151,62,0.2)',
  },
  topRight: { display: 'flex', alignItems: 'center', gap: 8 },
  balance: {
    fontSize: 14, fontWeight: 700, color: '#B8973E',
    fontFamily: "'JetBrains Mono', monospace",
    textShadow: '0 0 8px rgba(184,151,62,0.3)',
  },
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(5, 8, 15, 0.8)',
    backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
    zIndex: 200,
  },
  sideMenu: {
    position: 'fixed', top: 0, left: 0, bottom: 0, width: 260,
    background: 'rgba(10, 14, 26, 0.97)',
    backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
    borderRight: '1px solid rgba(184, 151, 62, 0.15)',
    zIndex: 300, transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    display: 'flex', flexDirection: 'column', overflowY: 'auto',
  },
  sideHeader: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '24px 20px 16px',
    borderBottom: '1px solid rgba(184, 151, 62, 0.1)',
  },
  sideLogoMark: {
    width: 24, height: 24,
    background: 'linear-gradient(135deg, #B8973E 0%, #9A7B30 100%)',
    border: '1px solid rgba(184,151,62,0.4)',
  },
  sideLogo: {
    fontSize: 12, fontWeight: 800, color: '#B8973E', letterSpacing: '0.2em',
  },
  sideBalance: {
    padding: '12px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  sideBalLabel: { fontSize: 9, color: '#4B5563', fontWeight: 700, letterSpacing: '0.1em' },
  sideBalVal: {
    fontSize: 16, fontWeight: 700, color: '#B8973E',
    fontFamily: "'JetBrains Mono', monospace",
    textShadow: '0 0 8px rgba(184,151,62,0.2)',
  },
  navLink: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '11px 20px',
    textDecoration: 'none', fontSize: 12, fontWeight: 600,
    letterSpacing: '0.04em',
    borderBottom: '1px solid rgba(255,255,255,0.03)',
    transition: 'all 0.15s ease',
  },
  sideFooter: {
    marginTop: 'auto', padding: '16px 20px',
    borderTop: '1px solid rgba(255,255,255,0.05)',
  },
  logoutBtn: {
    width: '100%', padding: '10px', borderRadius: 0,
    border: '1px solid rgba(239,68,68,0.3)',
    background: 'rgba(239,68,68,0.08)',
    color: '#EF4444', fontSize: 10, fontWeight: 700,
    letterSpacing: '0.12em', cursor: 'pointer',
    textTransform: 'uppercase',
  },
  bottomNav: {
    position: 'fixed', bottom: 0, left: 0, right: 0,
    display: 'flex', justifyContent: 'space-around', alignItems: 'center',
    background: 'rgba(10, 14, 26, 0.97)',
    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
    borderTop: '1px solid rgba(184, 151, 62, 0.12)',
    padding: '8px 0 6px', zIndex: 100,
  },
  bottomItem: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
    textDecoration: 'none', padding: '2px 0',
    transition: 'color 0.15s ease',
  },
};

const modalStyles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(5, 8, 15, 0.9)',
    backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', zIndex: 1000, padding: 20,
  },
  modal: {
    width: '100%', maxWidth: 400,
    background: 'rgba(13, 18, 32, 0.95)',
    backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
    borderRadius: 0,
    border: '1px solid rgba(184, 151, 62, 0.3)', padding: 28,
    boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
  },
  title: {
    fontSize: 16, fontWeight: 800, color: '#B8973E', textAlign: 'center',
    letterSpacing: '0.15em', margin: '8px 0',
    fontFamily: "'Inter', sans-serif",
  },
  text: { fontSize: 13, color: '#8B949E', textAlign: 'center', margin: '8px 0', lineHeight: 1.5 },
  stats: { display: 'flex', justifyContent: 'space-around', margin: '20px 0' },
  stat: { textAlign: 'center' },
  statLabel: { display: 'block', fontSize: 8, color: '#4B5563', fontWeight: 700, letterSpacing: '0.1em' },
  statValue: {
    fontSize: 22, fontWeight: 800, color: '#F0EDE6',
    fontFamily: "'JetBrains Mono', monospace",
  },
  reminder: { fontSize: 11, color: '#8B949E', textAlign: 'center', margin: '16px 0', lineHeight: 1.5 },
  btn: {
    width: '100%', padding: 13, borderRadius: 0,
    border: '1px solid #B8973E',
    background: 'linear-gradient(180deg, #B8973E 0%, #9A7B30 100%)',
    color: '#0A0E1A', fontSize: 12, fontWeight: 800,
    letterSpacing: '0.12em', cursor: 'pointer',
  },
  helpline: { fontSize: 9, color: '#4B5563', textAlign: 'center', marginTop: 16, letterSpacing: '0.05em' },
};
